import styled from '@emotion/styled/macro';
import {
  Badge,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Typography,
} from '@mui/material';
import {
  DBOrderDocument,
  DBWorkOrders,
  WorkOrdersStatus,
} from '@prisma/client';
import api from '@tyrio/api-factory';
import {
  DBCustomerOrderStatusEnum,
  DBWorkOrdersApi,
  IWorkOrderLineItem,
  OrderStatus,
  IReservedOnLocation,
} from '@tyrio/dto';
import { arrow, documentIcon, pdfFile } from '@tyrio/ui-library';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
  DocumentDetailsWrapper,
  FileIconNameWrapper,
  IconsWrapper,
  ReferenceWrapper,
} from '../../components/UploadDocuments/UploadDocuments';
import { WorkOrderStatusColors } from '../purchase-orders/helpers/generate-color';
import { useCancelWorkOrder } from './query/cancel-work-order';

const OrderTitle = {
  WORK_ORDER: 'Radni nalog',
};

export const WorkOrderDetails = ({
  workOrders,
  customerOrderId,
}: {
  workOrders?: DBWorkOrders[];
  customerOrderId?: string;
}) => {
  const [selectedOrder, setSelectedOrder] = useState<DBWorkOrders | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {}
  );

  const { mutateAsync: cancelWorkOrder, isLoading: isLoadingCancelWorkOrder } =
    useCancelWorkOrder();

  const {
    data: printDetails,
    isFetching,
    isSuccess,
    refetch,
  } = useQuery(
    ['get_work_orders_print_data', selectedOrder?.id],
    async () => {
      return await api.fetch<DBWorkOrdersApi['preparePrintData']>(
        'get_work_orders_print_data',
        {
          id: selectedOrder?.id ?? '',
        }
      );
    },
    {
      enabled: selectedOrder !== null,
    }
  );

  const { data: orderDocuments } = useQuery(
    ['get_work_orders_delivery_notes', customerOrderId],
    async () => {
      return await api.fetch<DBWorkOrdersApi['deliveryNotes']>(
        'get_work_orders_delivery_notes',
        { customerOrderId: customerOrderId ?? '' }
      );
    },
    {
      enabled: !!customerOrderId,
    }
  );

  // Get branch information for expanded work orders
  const { data: branchesInfo } = useQuery(
    ['get_branches_info'],
    async () => {
      return await api.fetch<any>('get_branches', {
        search: '',
        page: 1,
        pageSize: 1000,
      });
    },
    {
      enabled: Object.keys(expandedOrders).some((key) => expandedOrders[key]),
    }
  );

  useEffect(() => {
    if (
      selectedOrder &&
      !isFetching &&
      isSuccess &&
      printDetails &&
      printDetails !== '' &&
      printDetails.includes(selectedOrder.id)
    )
      window.open(printDetails, '_blank');
  }, [isFetching, isSuccess, printDetails, selectedOrder, selectedOrder?.id]);

  const handleArrowClick = (e: React.MouseEvent, item: DBWorkOrders) => {
    e.stopPropagation();
    setSelectedOrder(item);
    refetch();
    setTimeout(() => {
      if (
        item &&
        !isFetching &&
        isSuccess &&
        printDetails &&
        printDetails !== '' &&
        printDetails.includes(item.id)
      )
        window.open(printDetails, '_blank');
    }, 1000);
  };

  const handleWorkOrderClick = (item: DBWorkOrders) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [item.id]: !prev[item.id],
    }));
  };

  const handleCancelWorkOrder = (item: DBWorkOrders) => {
    if (isLoadingCancelWorkOrder) return;
    cancelWorkOrder({ workOrder: item });
  };

  const getBranchName = (erpBranchId: string) => {
    if (!branchesInfo || !branchesInfo.data) return erpBranchId;
    const branch = branchesInfo.data.find((b: any) => b.erpId === erpBranchId);
    return branch?.branchName || erpBranchId;
  };

  const getBranchId = (erpBranchId: string) => {
    if (!branchesInfo || !branchesInfo.data) return '';
    const branch = branchesInfo.data.find((b: any) => b.erpId === erpBranchId);
    return branch?.id.toString() || '';
  };

  const getLocationDisplay = (
    reservedOnLocation?: IReservedOnLocation[],
    itemQuantity?: number
  ) => {
    if (!reservedOnLocation || reservedOnLocation.length === 0) {
      return (
        <LocationItem style={{ color: '#d32f2f' }}>
          <RedDot />
          NO LOCATION
        </LocationItem>
      );
    }

    // Calculate total reserved quantity
    const totalReserved = reservedOnLocation.reduce(
      (sum, location) => sum + (location.reserved || 0),
      0
    );
    const remainingQuantity = (itemQuantity || 0) - totalReserved;

    const locationItems = reservedOnLocation.map((location, idx) => {
      // Remove first two parts from QR code (e.g., "SHELF-2-AA0-A0-2" becomes "AA0-A0-2")
      const locationParts = location.qrCode?.split('-') || [];
      const displayLocation =
        locationParts.length > 2
          ? locationParts.slice(2).join('-')
          : location.qrCode;

      return (
        <LocationItem key={idx}>
          <GreenDot />
          {displayLocation} (Qty: {location.reserved})
        </LocationItem>
      );
    });

    // If there's remaining quantity not reserved, show NO LOCATION for the rest
    if (remainingQuantity > 0) {
      locationItems.push(
        <LocationItem key="no-location" style={{ color: '#d32f2f' }}>
          <RedDot />
          NO LOCATION (Qty: {remainingQuantity})
        </LocationItem>
      );
    }

    return locationItems;
  };

  const isItemOrdered = (item: IWorkOrderLineItem) => {
    return (
      item.isSupplierStock || item.purchasing?.supplierSettings?.supplierId
    );
  };

  const renderLocationDisplay = (lineItem: IWorkOrderLineItem) => {
    // First check if there are locations (indicating product is received)
    const hasLocations = lineItem.reservedOnLocation && 
      lineItem.reservedOnLocation.length > 0;
    
    if (hasLocations) {
      // Product has locations, show them (product is received)
      return getLocationDisplay(
        lineItem.reservedOnLocation,
        lineItem.quantity
      );
    } else if (isItemOrdered(lineItem)) {
      // No locations but item is ordered, show ordered status
      return (
        <LocationItem style={{ color: '#1976d2' }}>
          <BlueDot />
          ORDERED FROM SUPPLIER
        </LocationItem>
      );
    } else {
      // No locations and not ordered, show no location
      return getLocationDisplay(
        lineItem.reservedOnLocation,
        lineItem.quantity
      );
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {workOrders &&
        workOrders?.map((item: DBWorkOrders, idx: number) => {
          const orderStatus = item.orderStatus as unknown as OrderStatus;
          const subStatus = orderStatus.subStatus;
          const status = orderStatus.status;
          const docId = item.erpOrderId;
          const erpDocNumber =
            item.erpDocNumber !== '' ? item.erpDocNumber : null;
          const isExpanded = expandedOrders[item.id] || false;

          const hasReservation =
            Array.isArray(item?.lineItems) &&
            (item?.lineItems as unknown as IWorkOrderLineItem[])?.every(
              (i) => i?.reservedOnLocation && !_.isEmpty(i?.reservedOnLocation)
            );

          const ritamInfo = [docId, erpDocNumber].filter(Boolean).join('-');

          return (
            <WorkOrderWrapper key={`${item.id}-${idx}`}>
              <DocumentDetailsWrapper
                key={`${item.id}-${idx}`}
                onClick={() => handleWorkOrderClick(item)}
                style={{
                  cursor: 'pointer',
                  border: 0,
                  borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                }}
              >
                <Badge
                  color={hasReservation ? 'success' : 'error'}
                  variant="dot"
                />
                <FileIconNameWrapper>
                  <img src={documentIcon} alt="file-icon" />
                  <p>
                    {OrderTitle.WORK_ORDER}{' '}
                    {docId && (
                      <span
                        style={{ color: '#919EAB', fontSize: '14px' }}
                      >{`(${ritamInfo}) `}</span>
                    )}
                  </p>
                </FileIconNameWrapper>

                <ReferenceWrapper>
                  <p>{item.orderNumber}</p>
                </ReferenceWrapper>

                {isFetching && selectedOrder?.id === item.id ? (
                  <CircularProgress
                    style={{
                      height: '20px',
                      width: '20px',
                    }}
                  />
                ) : (
                  <IconsWrapper>
                    <Chip
                      size="small"
                      label={item.externalStatus}
                      sx={{
                        bgcolor:
                          WorkOrderStatusColors[
                            item.externalStatus as unknown as WorkOrdersStatus
                          ] ?? 'white',
                        color:
                          item.externalStatus === 'NEW' ? '#919EAB' : 'white',
                        border:
                          item.externalStatus === 'NEW'
                            ? '1px solid #919EAB'
                            : 'none',
                        cursor: 'pointer',
                      }}
                    />

                    {status === DBCustomerOrderStatusEnum.ORDERED && (
                      <Chip
                        size="small"
                        label={orderStatus.status}
                        sx={{
                          bgcolor: '#9370DB',
                          color: 'white',
                          border: '1px solid #9370DB',
                          cursor: 'pointer',
                        }}
                      />
                    )}

                    {subStatus !== '' && (
                      <Chip
                        size="small"
                        label={orderStatus.subStatus}
                        sx={{
                          bgcolor: 'white',
                          color: '#919EAB',
                          border: '1px solid #919EAB',
                          cursor: 'pointer',
                        }}
                      />
                    )}
                    <img
                      src={arrow}
                      alt="arrow-icon"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleArrowClick(e, item)}
                    />
                  </IconsWrapper>
                )}
              </DocumentDetailsWrapper>

              <Collapse in={isExpanded}>
                <AccordionContent>
                  <InfoSection>
                    <InfoRow>
                      <InfoLabel>Branch:</InfoLabel>
                      <InfoValue>{getBranchName(item.erpBranchId)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Created by:</InfoLabel>
                      <InfoValue>
                        {item.stockOutData
                          ? (item.stockOutData as any)?.user || 'Unknown'
                          : 'Unknown'}
                        <DateTimeWrapper>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleDateString(
                                'en-GB',
                                {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                }
                              ) +
                              ' at ' +
                              new Date(item.createdAt).toLocaleTimeString(
                                'en-GB',
                                {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                }
                              )
                            : item.stockOutData
                            ? new Date(
                                (item.stockOutData as any)?.issueDate || ''
                              ).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }) +
                              ' at ' +
                              new Date(
                                (item.stockOutData as any)?.issueDate || ''
                              ).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'Unknown'}
                        </DateTimeWrapper>
                      </InfoValue>
                    </InfoRow>
                  </InfoSection>

                  <ItemsSection>
                    {(item.lineItems as unknown as IWorkOrderLineItem[])
                      ?.sort((a, b) => {
                        // Sort by EAN first, then by product name as secondary sort
                        const eanComparison = (a.ean || '').localeCompare(
                          b.ean || ''
                        );
                        if (eanComparison !== 0) return eanComparison;
                        return (a.productName || '').localeCompare(
                          b.productName || ''
                        );
                      })
                      ?.map((lineItem, lineIdx) => (
                        <ItemContainer key={lineIdx}>
                          <ItemHeader>
                            <ItemName>
                              {lineItem.brand} {lineItem.productName}
                            </ItemName>
                            <ItemQuantity>
                              Qty: {lineItem.quantity}
                            </ItemQuantity>
                          </ItemHeader>
                          <ItemDetails>
                            <DetailRow>
                              <DetailLabel>EAN:</DetailLabel>
                              <DetailValue>{lineItem.ean}</DetailValue>
                            </DetailRow>
                            <DetailRow>
                              <DetailLabel>SKU:</DetailLabel>
                              <DetailValue>{lineItem.sku}</DetailValue>
                            </DetailRow>
                            <DetailRow>
                              <DetailLabel>DOT:</DetailLabel>
                              <DetailValue>{lineItem.dot}</DetailValue>
                            </DetailRow>
                          </ItemDetails>
                          <LocationSection>
                            <div>
                              {renderLocationDisplay(lineItem)}
                            </div>
                            <StockListLink 
                              href={`/dashboard/stock-list?searchType=ean&warehouseId=${getBranchId(item.erpBranchId)}&searchKeyword=${lineItem.ean}`} 
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Stock
                            </StockListLink>
                          </LocationSection>
                        </ItemContainer>
                      ))}
                  </ItemsSection>
                </AccordionContent>
              </Collapse>

              <ButtonWrapper
                style={{
                  borderRadius: isExpanded ? '0 0 8px 8px' : '8px',
                  borderTop: isExpanded ? '1px solid #d2d4d2' : 'none',
                }}
              >
                <Button
                  color="error"
                  onClick={() => handleCancelWorkOrder(item)}
                  disabled={
                    status === DBCustomerOrderStatusEnum.CANCELLED ||
                    isLoadingCancelWorkOrder
                  }
                >
                  CANCEL
                </Button>
              </ButtonWrapper>
            </WorkOrderWrapper>
          );
        })}

      {orderDocuments &&
        orderDocuments.map((item: DBOrderDocument, idx: number) => (
          <DocumentDetailsWrapper
            key={`${item.id} - ${idx}`}
            onClick={() => window.open(item.url, '_blank')}
            style={{ marginTop: workOrders ? '10px' : '' }}
          >
            <FileIconNameWrapper>
              <img src={pdfFile} alt="pdf-icon" />
              <p>{item.description}</p>
            </FileIconNameWrapper>
            <ReferenceWrapper>
              <p>{item.reference}</p>
            </ReferenceWrapper>
            <IconsWrapper style={{ cursor: 'pointer' }}>
              <Chip
                size="small"
                label={'Poslano'}
                sx={{
                  bgcolor: '#3EB274',
                  color: 'white',
                  cursor: 'pointer',
                }}
              />
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'flex', justifyContent: 'center' }}
                  download
                >
                  <img
                    src={arrow}
                    alt="arrow-icon"
                    style={{ cursor: 'pointer' }}
                  />
                </a>
              )}
            </IconsWrapper>
          </DocumentDetailsWrapper>
        ))}
    </div>
  );
};

export const StatusWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 40%;
`;

const WorkOrderWrapper = styled.div`
  border-radius: 8px;
  border: 1px solid #d2d4d2;
`;

const ButtonWrapper = styled.div`
  background: #f9fafb;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-right: 10px;
  padding-top: 4px;
  padding-bottom: 4px;
`;

const AccordionContent = styled.div`
  padding: 16px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
`;

const InfoSection = styled.div`
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  margin-bottom: 8px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  min-width: 100px;
  color: #333;
`;

const InfoValue = styled.span`
  color: #666;
`;

const ItemsSection = styled.div`
  margin-top: 16px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
`;

const ItemContainer = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ItemName = styled.span`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const ItemQuantity = styled.span`
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const ItemDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #888;
  text-transform: uppercase;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: #333;
  margin-top: 2px;
`;

const LocationSection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StockListLink = styled.a`
  color: #1976d2;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

const LocationItem = styled.div`
  font-size: 12px;
  color: #333;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RedDot = styled.div`
  width: 6px;
  height: 6px;
  background-color: #d32f2f;
  border-radius: 50%;
  display: inline-block;
`;

const GreenDot = styled.div`
  width: 6px;
  height: 6px;
  background-color: #3eb274;
  border-radius: 50%;
  display: inline-block;
`;

const BlueDot = styled.div`
  width: 6px;
  height: 6px;
  background-color: #1976d2;
  border-radius: 50%;
  display: inline-block;
`;

const DateTimeWrapper = styled.span`
  font-size: 12px;
  color: #919eab;
  margin-left: 8px;
`;
